import {
  PLAN_CATALOG,
  type CreatePlatformTenantResponse,
  type PlanCode,
  type TenantStatus,
} from '@horevia/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ChipFilter,
  Container,
  EmptyState,
  ErrorState,
  Input,
  NoPermissionState,
  PageHeader,
  Table,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Toolbar,
  Tr,
} from '@horevia/ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Building2, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/auth/session';
import { tenantStatusTone } from '@/features/platform/labels';
import { platformTenantsQueryOptions } from '@/features/platform/queries';
import { TenantFormDrawer } from '@/features/platform/TenantFormDrawer';
import { DeliveryLinkDialog } from '@/features/users/DeliveryLinkDialog';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatRelative } from '@/lib/format';

interface TenantsSearch {
  /** Ouvre le tiroir « Nouveau tenant » (palette de commandes, vue d'ensemble). */
  new?: boolean;
}

export const Route = createFileRoute('/_app/platform/tenants/')({
  validateSearch: (search: Record<string, unknown>): TenantsSearch =>
    search['new'] === true || search['new'] === 'true' ? { new: true } : {},
  component: PlatformTenantsPage,
});

const STATUSES: TenantStatus[] = ['TRIAL', 'ACTIVE', 'SUSPENDED'];

/** Tenants de la plateforme (ADR-021) : liste, filtres, provisionnement. */
function PlatformTenantsPage() {
  const { t, i18n } = useTranslation(['platform', 'common', 'errors', 'settings']);
  const session = useSession();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const canRead = session.can('platform.read_health');
  const canManage = session.can('platform.manage_tenants');
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState<TenantStatus | undefined>(undefined);
  const [planCode, setPlanCode] = useState<PlanCode | undefined>(undefined);
  const [drawerOpen, setDrawerOpen] = useState(Boolean(search.new) && canManage);
  const [created, setCreated] = useState<CreatePlatformTenantResponse | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [term]);

  useEffect(() => {
    if (search.new && canManage) setDrawerOpen(true);
  }, [search.new, canManage]);

  const filters = useMemo(
    () => ({ q: debounced || undefined, status, planCode }),
    [debounced, status, planCode],
  );
  const query = useInfiniteQuery({ ...platformTenantsQueryOptions(filters), enabled: canRead });
  const rows = query.data?.pages.flatMap((page) => page.data) ?? [];

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader
        title={t('platform:tenants.title')}
        subtitle={t('platform:tenants.subtitle')}
        breadcrumb={[
          { label: t('platform:title'), link: <Link to="/platform">{t('platform:title')}</Link> },
          { label: t('platform:tenants.title') },
        ]}
        actions={
          canManage ? (
            <Button variant="primary" onClick={() => setDrawerOpen(true)}>
              <Plus className="ico" aria-hidden="true" />
              {t('platform:tenants.new')}
            </Button>
          ) : null
        }
      />

      <Card>
        <CardBody density="flush">
          <Toolbar>
            <div className="row row--wrap" style={{ gap: 8, flex: 1 }}>
              <Input
                aria-label={t('platform:tenants.search')}
                placeholder={t('platform:tenants.search')}
                value={term}
                onChange={(event) => setTerm(event.target.value)}
                style={{ maxWidth: 280 }}
              />
              <select
                className="select"
                aria-label={t('platform:tenants.allPlans')}
                value={planCode ?? ''}
                onChange={(event) =>
                  setPlanCode((event.target.value || undefined) as PlanCode | undefined)
                }
              >
                <option value="">{t('platform:tenants.allPlans')}</option>
                {PLAN_CATALOG.map((plan) => (
                  <option key={plan.code} value={plan.code}>
                    {plan.name}
                  </option>
                ))}
              </select>
              {STATUSES.map((item) => (
                <ChipFilter
                  key={item}
                  active={status === item}
                  onClick={() => setStatus((current) => (current === item ? undefined : item))}
                >
                  {t(`platform:status.${item}`)}
                </ChipFilter>
              ))}
            </div>
          </Toolbar>
          {query.isPending ? (
            <TableSkeleton rows={5} columns={7} label={t('common:states.loading')} />
          ) : query.isError ? (
            <ErrorState
              title={t('common:states.errorTitle')}
              description={errorMessage(query.error, t)}
              traceId={errorTraceId(query.error)}
              retryLabel={t('common:actions.retry')}
              onRetry={() => void query.refetch()}
              traceCopyLabel={t('common:actions.copy')}
              traceCopiedLabel={t('common:actions.copied')}
            />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Building2 className="ico" aria-hidden="true" />}
              title={t('platform:tenants.empty')}
              description={canManage ? t('platform:tenants.emptyHint') : undefined}
            />
          ) : (
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>{t('platform:tenants.columns.tenant')}</Th>
                    <Th>{t('platform:tenants.columns.status')}</Th>
                    <Th>{t('platform:tenants.columns.plan')}</Th>
                    <Th>{t('platform:tenants.columns.modules')}</Th>
                    <Th align="right">{t('platform:tenants.columns.properties')}</Th>
                    <Th align="right">{t('platform:tenants.columns.users')}</Th>
                    <Th>{t('platform:tenants.columns.lastLogin')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <Tr key={row.id}>
                      <Td>
                        <div className="stack" style={{ gap: 2 }}>
                          <Link to="/platform/tenants/$tenantId" params={{ tenantId: row.id }}>
                            <strong>{row.tradeName ?? row.legalName}</strong>
                          </Link>
                          <span className="t-12 muted">
                            {row.legalName} · {row.slug} · {row.countryCode} · {row.currencyCode}
                          </span>
                        </div>
                      </Td>
                      <Td>
                        <Badge tone={tenantStatusTone(row.status)}>
                          {t(`platform:status.${row.status}`)}
                        </Badge>
                      </Td>
                      <Td>{row.subscription?.planName ?? '—'}</Td>
                      <Td>
                        <div className="row row--wrap" style={{ gap: 4 }}>
                          {row.modules.map((code) => (
                            <Badge key={code} tone="neutral">
                              {t(`settings:modules.names.${code}`)}
                            </Badge>
                          ))}
                        </div>
                      </Td>
                      <Td numeric align="right">
                        {row.propertiesCount}
                      </Td>
                      <Td numeric align="right">
                        {row.usersCount}
                      </Td>
                      <Td>
                        {row.lastLoginAt
                          ? formatRelative(row.lastLoginAt, i18n.language)
                          : t('platform:tenants.neverLoggedIn')}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
          {query.hasNextPage ? (
            <div className="row" style={{ justifyContent: 'center', padding: 12 }}>
              <Button
                variant="secondary"
                disabled={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {t('common:actions.loadMore')}
              </Button>
            </div>
          ) : null}
        </CardBody>
      </Card>

      {canManage ? (
        <TenantFormDrawer
          open={drawerOpen}
          onOpenChange={(open) => {
            setDrawerOpen(open);
            if (!open && search.new) void navigate({ to: '/platform/tenants', search: {} });
          }}
          onCreated={(result) => setCreated(result)}
        />
      ) : null}

      <DeliveryLinkDialog
        link={created?.activation ?? null}
        recipient={
          created?.owners[0]
            ? { fullName: created.owners[0].fullName, email: created.owners[0].email }
            : null
        }
        onClose={() => {
          const id = created?.id;
          setCreated(null);
          if (id) void navigate({ to: '/platform/tenants/$tenantId', params: { tenantId: id } });
        }}
      />
    </Container>
  );
}
